import { Handshake, Mail, MapPin, Phone } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Link, useLocation } from "react-router";

import { mapsHref } from "@/lib/linking";
import { OpenMailComposeLink } from "@/modules/mail/OpenMailComposeLink";
import { CrmPhoneLink } from "@/modules/voice/CrmPhoneLink";
import { useGetIdentity, useGetOne } from "ra-core";
import type { Company, Contact } from "../types";
import { canUseCrmPermission } from "../providers/commons/crmPermissions";
import { AddTask } from "../tasks/AddTask";
import { TagsListEdit } from "./TagsListEdit";
import { Avatar } from "./Avatar";
import { ConvertLeadButton } from "@/modules/leads/ConvertLeadButton";
import { getClientShowPath, getPersonShowPath } from "@/app/routing";
import { OpenClientSmsButton } from "@/modules/messages/OpenClientSmsButton";
import { SendFormButton } from "@/modules/forms/share/SendFormButton";

export const ContactHeader = ({
  record,
  locationSearch,
  onEdit,
  isMobile = false,
  embedded = false,
  onClose,
}: {
  record: Contact;
  locationSearch: string;
  onEdit: () => void;
  isMobile?: boolean;
  embedded?: boolean;
  onClose?: () => void;
}) => {
  const location = useLocation();
  const { data: identity } = useGetIdentity();
  const canManageSales = canUseCrmPermission(identity as any, "sales.manage");

  const primaryEmail =
    record.email_jsonb?.find((entry) => entry.email?.trim())?.email?.trim() ??
    "";
  const primaryPhone =
    record.phone_jsonb?.find((entry) => entry.number?.trim())?.number?.trim() ??
    "";
  const emailLink = primaryEmail ? primaryEmail : "";
  const phoneLink = primaryPhone ? primaryPhone : null;
  const address = record.address?.trim() ?? "";
  const statusLabel = record.status
    ? record.status.charAt(0).toUpperCase() + record.status.slice(1)
    : "Active";

  return (
    <div className="rounded-xl border border-border bg-muted/20 p-5">
      <div
        className={cn(
          "flex gap-4",
          isMobile ? "flex-col" : "items-start justify-between",
        )}
      >
        <div className="flex min-w-0 gap-4">
          <Avatar />
          <div className="min-w-0 space-y-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h1
                className={cn(
                  "truncate font-semibold",
                  embedded ? "text-base" : "text-lg",
                )}
              >
                {record.first_name} {record.last_name}
              </h1>
              <Badge variant="secondary">{statusLabel}</Badge>
              {!embedded ? <TagsListEdit hideButton /> : null}
            </div>

            <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span className="truncate">
                {record.title || "Contact"}
                {record.company_id != null && " at "}
                {record.company_id != null ? (
                  embedded ? (
                    <span>{record.company_name ?? "Account"}</span>
                  ) : (
                    <Link
                      to={getClientShowPath(record.company_id)}
                      state={{ from: `${location.pathname}${locationSearch}` }}
                      className="link-action"
                    >
                      {record.company_name ?? "Account"}
                    </Link>
                  )
                ) : null}
              </span>
              <span className="inline-flex items-center gap-2">
                <Phone className="size-4" />
                {phoneLink ? (
                  <CrmPhoneLink
                    phone={phoneLink}
                    contactId={record.id}
                    className="link-action"
                  />
                ) : (
                  "—"
                )}
              </span>
              <span className="inline-flex items-center gap-2">
                <Mail className="size-4" />
                {emailLink ? (
                  <OpenMailComposeLink
                    to={emailLink}
                    contactId={record.id}
                    companyId={record.company_id ?? undefined}
                    className="break-all"
                  >
                    {primaryEmail}
                  </OpenMailComposeLink>
                ) : (
                  "—"
                )}
              </span>
              <span className="inline-flex min-w-0 items-center gap-2">
                <MapPin className="size-4 shrink-0" />
                {address ? (
                  <a
                    href={mapsHref(address)}
                    target="_blank"
                    rel="noreferrer"
                    className="link-action truncate"
                  >
                    {address}
                  </a>
                ) : (
                  "—"
                )}
              </span>
            </div>
            <ReferredByLine record={record} />
          </div>
        </div>

        <div
          className={cn(
            "flex gap-2",
            isMobile ? "flex-wrap" : "items-center justify-end",
          )}
        >
          {canManageSales ? (
            <>
              {!embedded ? <ConvertLeadButton record={record} /> : null}
              {!embedded ? <TagsListEdit buttonOnly /> : null}
              {!embedded ? <AddTask display="chip" /> : null}
              <OpenClientSmsButton contact={record} />
              {!embedded ? (
                <SendFormButton
                  context={{
                    type: record.status === "lead" ? "lead" : "contact",
                    contact_id: Number(record.id),
                    company_id:
                      record.company_id != null
                        ? Number(record.company_id)
                        : undefined,
                    resourceName:
                      `${record.first_name ?? ""} ${record.last_name ?? ""}`.trim(),
                  }}
                />
              ) : null}
              <Button
                onClick={onEdit}
                size={embedded ? "sm" : "default"}
                variant="secondary"
              >
                Edit
              </Button>
              {embedded && onClose ? (
                <Button onClick={onClose} size="sm" variant="ghost">
                  Close
                </Button>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const ReferredByLine = ({ record }: { record: Contact }) => {
  const refContactId = record.referred_by_contact_id;
  const refCompanyId = record.referred_by_company_id;

  const { data: refContact } = useGetOne<Contact>(
    "contacts",
    { id: refContactId ?? "" },
    { enabled: refContactId != null },
  );
  const { data: refCompany } = useGetOne<Company>(
    "companies",
    { id: refCompanyId ?? "" },
    { enabled: refCompanyId != null },
  );

  if (!refContact && !refCompany) return null;

  const contactName = refContact
    ? `${refContact.first_name ?? ""} ${refContact.last_name ?? ""}`.trim() ||
      "Unnamed"
    : null;

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
      <Handshake className="size-4 shrink-0" />
      <span>Referido por:</span>
      {refContact ? (
        <Link to={getPersonShowPath(refContact)} className="link-action">
          {contactName}
        </Link>
      ) : null}
      {refContact && refCompany ? <span>·</span> : null}
      {refCompany ? (
        <Link to={getClientShowPath(refCompany.id)} className="link-action">
          {refCompany.name}
        </Link>
      ) : null}
    </div>
  );
};
